import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, Drawer, InputRef, message, Modal, Space, Table, Typography } from "antd";
import { EyeOutlined, ReloadOutlined } from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import { SorterResult } from "antd/es/table/interface";
import { ColumnsType } from "antd/es/table";
import moment from "moment";

import { UserLogsService } from 'services'
import { UserLogsRecord } from "types";
import CustomBreadcrumb from "components/CustomBreadcrumb";
import { GetUserLogsRecordsParams } from "services/user-logs";
import UserLogsFilters from "../components/UserLogsFilters";
import { UserLogsFilter } from "../types";
import { objectToUrlParams, urlParamsToObject } from "../../../utils/url-utils";
import { history } from "../../../utils/history";
import { deepCamelCaseKeys } from "../../../utils/objects";
import { getColumnSearchProps } from "../../../components/TableSearch";

// ----- Global variables -----
const DEFAULT_PAGE_SIZE = 100


const UserLogsListPage = () => {
    // ----- Navigation -----
    const [searchParams] = useSearchParams();

    // ----- Data -----
    const [userLogs, setUserLogs] = useState<UserLogsRecord[]>([])
    const [selectedUserLogId, setSelectedUserLogId] = useState<number | null>(null)
    const [loading, setLoading] = useState<boolean>(false)

    // ----- Filters -----
    const [filters, setFilters] = useState<UserLogsFilter>()

    // ----- Modal -----
    const [isModalOpen, setIsModalOpen] = useState(false);

    // ----- Table -----
    const [totalItems, setTotalItems] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)// todo: add search field

    const searchInput = useRef<InputRef>(null);
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const [search,] = useState<string>('')
    const [sortField, setSortField] = useState<string | undefined>()
    const [sortOrder, setSortOrder] = useState<'ascend' | 'descend' | undefined>()

    // ----- Drawer -----
    // todo: if mac id present in the url no drawer is needed
    const [open, setOpen] = useState(true);

    // ----- Hooks -----
    useEffect(() => {
        // fetch data from server
        fetchData()
    }, [currentPage, search, sortField, sortOrder, filters])

    useEffect(() => {
        // get filters from url
        const filtersInUrl = urlParamsToObject<UserLogsFilter>(searchParams)
        const types = Array.isArray(filtersInUrl?.types) ? filtersInUrl?.types : (filtersInUrl?.types ? [filtersInUrl?.types] : [])
        if (filtersInUrl) {
            // Update the filters state
            setFilters(prevFilters => ({
                ...prevFilters,
                ...filtersInUrl,
                types,
            }));
        }
        // Run this effect whenever 'searchParams' changes
    }, [searchParams])

    // ----- Drawer -----
    const showDrawer = () => {
        setOpen(true);
    };

    const onClose = () => {
        setOpen(false);
    };
    // ----- End Drawer -----

    // ----- Modal -----
    const showModal = () => {
        setIsModalOpen(true);
    };

    const handleOk = () => {
        setIsModalOpen(false);
        setSelectedUserLogId(null);
    };

    const handleCancel = () => {
        setIsModalOpen(false);
        setSelectedUserLogId(null);
    };
    // ----- End Modal -----

    const fetchData = async () => {
        if (!filters?.macAddress) return
        setLoading(true)
        try {
            const params: GetUserLogsRecordsParams = {
                mac_address: filters?.macAddress || '', // "FDC6E5B9CFD1",
                types: filters?.types || [],
                ...(filters?.startDate ? { start_date: filters?.startDate } : {}),
                ...(filters?.endDate ? { end_date: filters?.endDate } : {}),
                page: currentPage,
                page_size: pageSize,
            }
            const response = await UserLogsService.getUserLogsRecords(params)
            if (!response)
                throw Error("Invalid response")
            const { items, count } = response

            const formattedDiagnosticLogs = items?.map((item: UserLogsRecord) => ({
                ...deepCamelCaseKeys<UserLogsRecord>(item),
                key: `user-log-record-${item?.id}`,
            }))
            setUserLogs(formattedDiagnosticLogs)
            setTotalItems(count)
        } catch (err) {
            // Show error message
            console.error('Error getting user logs')
            message.error(`Failed to get user-logs`);
        }
        setLoading(false)
    }

    // ----- Handlers -----
    const handleViewRecordDetails = (record: UserLogsRecord) => {
        setSelectedUserLogId(record.id)
        showModal()
    }

    const handleOnFilter = (values: UserLogsFilter) => {
        setFilters(values)
        setOpen(false)
        // add filters to url
        const url = objectToUrlParams(values);
        history?.navigate?.(url);
    }

    const columns: ColumnsType<UserLogsRecord> = [
        {
            title: 'Id',
            dataIndex: 'id',
            key: 'id',
            ...getColumnSearchProps({
                searchInput,
                setSearchText,
                setSearchedColumn,
                searchedColumn,
                searchText,
                dataIndex: 'id'
            }),
        },
        {
            title: 'Type',
            dataIndex: 'name',
            key: 'name',
            render: (value) => value?.replace('Record', '')
        },
        {
            title: 'Timestamp',
            dataIndex: 'ts',
            key: 'ts',
            render: (value) => <>{value}</>
        },
        {
            title: 'Info',
            dataIndex: 'reason',
            key: 'reason',
            render: (value) => <Typography.Paragraph>{value}</Typography.Paragraph>
        }, {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            render: (_: object, record: UserLogsRecord) => {
                return (
                    <Button onClick={() => handleViewRecordDetails(record)} type="primary" icon={<EyeOutlined />} />
                )
            }

        }
    ]

    const selectedUserLog = userLogs.find(item => item.id === selectedUserLogId) || null
    return (
        <>
            <CustomBreadcrumb list={['User logs', 'List',]} />
            <Card title="User logs"
                extra={
                    <Space>
                        <Button
                            loading={loading}
                            onClick={() => fetchData()}
                            icon={<ReloadOutlined />} />
                        <Button type="primary"
                            loading={loading}
                            onClick={() => showDrawer()}
                        >
                            Filters
                        </Button>
                    </Space>
                }
            >
                <Table<UserLogsRecord>
                    columns={columns}
                    dataSource={userLogs}
                    loading={loading}
                    size="small"
                    bordered
                    pagination={{
                        pageSize: pageSize,
                        current: currentPage,
                        total: totalItems,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '30', '50', '100'],
                        showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} items`
                    }}
                    onChange={(pagination, filters, sorter: SorterResult<UserLogsRecord> | SorterResult<UserLogsRecord>[],) => {
                        // setFilters(filters)
                        setPageSize(pagination.pageSize || DEFAULT_PAGE_SIZE);
                        setCurrentPage(pagination.current || 1);
                        console.log('pagination.current ', pagination.current);
                        if (typeof sorter === 'object') {
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            setSortField(sorter?.field?.toString())
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            setSortOrder(sorter?.order?.toString())
                        }
                    }}
                />
            </Card>
            <Drawer
                title="Filter"
                placement="left"
                closable={false}
                onClose={onClose}
                open={open}
                key={"DrawerFilter"}
            >
                <UserLogsFilters onSubmit={handleOnFilter} filters={filters} loading={loading} />
            </Drawer>
            <Modal title="Your JSON Data" open={isModalOpen} onOk={handleOk} onCancel={handleCancel}>
                <pre>{selectedUserLog && JSON.stringify(selectedUserLog, null, 2)}</pre>
            </Modal>
        </>
    )
}

export default UserLogsListPage
